import { mergeTemplatePage } from '@/utils/exportDeliveryOrderPrintExcel.js'
import { getPhysicalPaperMm } from '@/utils/printPageGeometry.js'
import {
  collectPurePrintPages,
  rasterizePurePrintPages,
  stripPurePrintPageChrome
} from '@/utils/purePagedStatementRender.js'

function jsPdfOptionsFromPage (page) {
  const p = mergeTemplatePage(page)
  const mt = Number(p.marginTop ?? 10) || 10
  const mr = Number(p.marginRight ?? 10) || 10
  const mb = Number(p.marginBottom ?? 10) || 10
  const ml = Number(p.marginLeft ?? 10) || 10
  const orient = p.orientation === 'landscape' ? 'landscape' : 'portrait'
  const ps = String(p.paperSize || '').toLowerCase()

  let jsPdf = { unit: 'mm', orientation: orient }

  if (ps === 'custom') {
    let w = Number(p.customWidthMm) || 210
    let h = Number(p.customHeightMm) || 297
    if (orient === 'landscape') {
      const t = w
      w = h
      h = t
    }
    jsPdf.format = [w, h]
  } else if (p.paperSize === 'A3') {
    jsPdf.format = 'a3'
  } else if (p.paperSize === 'Letter') {
    jsPdf.format = 'letter'
  } else {
    jsPdf.format = 'a4'
  }

  return {
    margin: [mt, ml, mb, mr],
    jsPDF: jsPdf,
    html2canvas: {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      scrollX: 0,
      scrollY: typeof window !== 'undefined' ? -window.scrollY : 0
    },
    image: { type: 'jpeg', quality: 0.92 },
    pagebreak: { mode: ['css', 'legacy'] }
  }
}

/** 多页栅格化参数（与导出 PDF 一致） */
export function getMultiPageRasterizeOptions (templateConfig) {
  const baseOpts = jsPdfOptionsFromPage(mergeTemplatePage(templateConfig?.page))
  return {
    scale: baseOpts.html2canvas?.scale ?? 2,
    jpegQuality: baseOpts.image?.quality ?? 0.92
  }
}

/**
 * 从打印预览 DOM 克隆内容，版式与屏幕预览一致（多页账单每页独立 .pure-print-page）。
 */
export function buildPrintPreviewPdfRoot (previewContentEl, { multiPaged = false } = {}) {
  const root = document.createElement('div')
  root.className = 'print-preview-pdf-root'
  root.style.cssText = 'margin:0;padding:0;background:#fff;line-height:0;font-size:0;'

  if (multiPaged) {
    const pages = collectPurePrintPages(previewContentEl)
    pages.forEach((pageEl) => {
      const clone = pageEl.cloneNode(true)
      stripPurePrintPageChrome(clone)
      root.appendChild(clone)
    })
    return root
  }

  const page = previewContentEl?.querySelector('.print-page')
  if (page) {
    const clone = page.cloneNode(true)
    clone.style.boxShadow = 'none'
    root.appendChild(clone)
  }
  return root
}

function defaultPdfFilename (printData, templateType) {
  const d = printData || {}
  if (templateType === 'payment_request' && d.statement_no) {
    return `${d.statement_no}_账单.pdf`
  }
  if (templateType === 'delivery_order' && (d.delivery_order_no || d.order_no)) {
    return `${d.delivery_order_no || d.order_no}_送货单.pdf`
  }
  if (d.order_no) return `${d.order_no}_打印.pdf`
  return '打印预览.pdf'
}

/** 将栅格化页图写入 jsPDF（与导出 PDF 多页逻辑一致） */
export async function createMultiPagePdfFromRasterImages (pageImages, baseOpts) {
  const [{ jsPDF }] = await Promise.all([import('jspdf')])
  const jsPdfCfg = baseOpts.jsPDF || { unit: 'mm', format: 'a4', orientation: 'portrait' }
  const pdf = new jsPDF(jsPdfCfg)
  const pdfW = pdf.internal.pageSize.getWidth()
  const pdfH = pdf.internal.pageSize.getHeight()
  const list = Array.isArray(pageImages) ? pageImages : []

  list.forEach(({ dataUrl }, i) => {
    if (i > 0) {
      pdf.addPage(jsPdfCfg.format, jsPdfCfg.orientation)
    }
    pdf.addImage(dataUrl, 'JPEG', 0, 0, pdfW, pdfH, undefined, 'FAST')
  })

  return pdf
}

/** 多页账单：由已栅格化的页图生成 PDF Blob（供浏览器打印，页数与导出 PDF 一致） */
export async function buildMultiPageRasterPdfBlob ({ pageImages, templateConfig } = {}) {
  const pageCfg = mergeTemplatePage(templateConfig?.page)
  const baseOpts = jsPdfOptionsFromPage(pageCfg)
  const pdf = await createMultiPagePdfFromRasterImages(pageImages, baseOpts)
  return pdf.output('blob')
}

/** 多页账单：逐页栅格化再写入 PDF，避免整段高度溢出产生末尾空白页 */
async function exportMultiPagePrintPdf (pageElements, baseOpts, filename, paperMm) {
  const scale = baseOpts.html2canvas?.scale ?? 2
  const jpegQuality = baseOpts.image?.quality ?? 0.92
  const paper = paperMm || { w: 210, h: 297 }

  const pageImages = await rasterizePurePrintPages(pageElements, {
    scale,
    jpegQuality,
    paperWmm: paper.w,
    paperHmm: paper.h
  })

  const pdf = await createMultiPagePdfFromRasterImages(pageImages, baseOpts)
  pdf.save(filename)
}

/**
 * 导出与屏幕预览一致的 PDF（html2canvas + jsPDF），跨浏览器可直接打印。
 */
export async function exportPrintPreviewPdf ({
  previewContentEl,
  templateConfig,
  printData,
  templateType,
  isMultiPageStatementPreview = false,
  filename
} = {}) {
  const pageCfg = mergeTemplatePage(templateConfig?.page)
  const baseOpts = jsPdfOptionsFromPage(pageCfg)
  const outName = filename || defaultPdfFilename(printData, templateType)

  const pageElements = collectPurePrintPages(previewContentEl)
  const multiPaged =
    isMultiPageStatementPreview ||
    pageElements.length > 1

  if (multiPaged && pageElements.length > 0) {
    const paper = getPhysicalPaperMm(pageCfg)
    await exportMultiPagePrintPdf(pageElements, baseOpts, outName, paper)
    return
  }

  const mod = await import('html2pdf.js')
  const html2pdf = typeof mod.default === 'function' ? mod.default : mod
  const el = buildPrintPreviewPdfRoot(previewContentEl, { multiPaged: false })
  if (!el.childNodes.length) {
    throw new Error('暂无打印内容')
  }

  const opt = {
    ...baseOpts,
    filename: outName,
    html2canvas: {
      ...baseOpts.html2canvas,
      scale: typeof baseOpts.html2canvas?.scale === 'number' ? baseOpts.html2canvas.scale : 2
    }
  }

  const host = document.createElement('div')
  host.setAttribute('aria-hidden', 'true')
  host.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483646',
    'pointer-events:none',
    'overflow:auto',
    'background:#fff',
    'opacity:0.015'
  ].join(';')
  host.appendChild(el)
  document.body.appendChild(host)

  try {
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      await document.fonts.ready.catch(() => {})
    }
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))
    await new Promise((r) => setTimeout(r, 120))
    await html2pdf().set(opt).from(el).save()
  } finally {
    if (host.parentNode) {
      host.parentNode.removeChild(host)
    }
  }
}

export {
  collectPurePrintPages,
  rasterizePurePrintPages,
  clonePurePrintPagesHtml,
  buildPureDomPrintDocumentHtml,
  buildPrintPreparingDocumentHtml,
  buildRasterPrintDocumentHtml,
  loadPdfBlobInPrintWindow
} from '@/utils/purePagedStatementRender.js'
